import {
  faArrowDown,
  faArrowUp,
  faClose,
} from "@fortawesome/pro-regular-svg-icons";
import { observer } from "mobx-react-lite";

import { useFloatingWindowsActions } from "src/base-components/FloatingWindow/hooks";
import { FloatingWindowProps } from "src/base-components/FloatingWindow/store";
import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import {
  FloatingWindowInternal,
  useFloatingWindowPosition,
} from "src/datasets/DatasetTable/FloatingWindow";
import { useCanvasSearchTermParam } from "src/flowGraph/useCanvasSearchTermParam";

type WindowData = {
  searchQuery: string;
};

const assertWindowData = (data: unknown): data is WindowData => {
  return Boolean(data && typeof data === "object");
};

export const FlowGraphSearchWindow: React.FC<FloatingWindowProps> = observer(
  ({ computePosition, id, data: windowData }) => {
    if (!assertWindowData(windowData)) {
      throw new Error("Invalid floating window data", windowData);
    }

    const { searchTerm, setSearchTerm, clearSearchTerm } =
      useCanvasSearchTermParam();

    const { close: closeWindow } = useFloatingWindowsActions();
    const { position, windowRef } = useFloatingWindowPosition(computePosition);

    const handleSearchTermChange = (
      e: React.ChangeEvent<HTMLInputElement>,
    ): void => {
      setSearchTerm(e.target.value);
    };

    const handleClose = () => {
      clearSearchTerm();
      closeWindow(id);
    };

    return (
      <FloatingWindowInternal
        ref={windowRef}
        height="h-16"
        isPinned={false}
        maximizable={false}
        style={{
          inset: "0px auto auto 0px",
          transform: position,
        }}
        title=""
        width="w-90"
        draggable
        fullBodyWidth
        headerless
        onClose={handleClose}
      >
        <div className="flex h-full items-center gap-x-3 px-4" data-grabbable>
          <Input
            placeholder="Search for a field"
            type="text"
            value={searchTerm}
            fullWidth
            onChange={handleSearchTermChange}
          />
          <Icon color="text-gray-500" icon={faArrowUp} size="xs" />
          <Icon color="text-gray-500" icon={faArrowDown} size="xs" />
          <Icon
            color="text-gray-500"
            icon={faClose}
            size="xs"
            onClick={handleClose}
          />
        </div>
      </FloatingWindowInternal>
    );
  },
);
